// Shared UI kit for the HR module — keeps every HR screen visually consistent
// with the rest of Khyati Portal (navy #2D2C72, red #E22D2D, warm bg, violet).
import React from 'react';
import { C } from '../../theme';

export const HRC = {
  sidebar: '#2D2C72', navy: '#3B3A8F', red: '#E22D2D',
  pageBg: '#F7F6F3', violet: '#EEEDF8', card: '#FFFFFF',
  text: '#1C1C1C', muted: '#6B6B6B', border: '#E8E7E7',
  green: '#1D9E75', amber: '#BA7517', ...C,
};

export const money = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
export const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
export const fmtTime = (t) => t ? String(t).slice(0, 5) : '—';

// Card container.
export const Card = ({ children, style }) => (
  <div style={{ background: '#fff', borderRadius: 10, border: `1px solid ${HRC.border}`, padding: 18, ...style }}>{children}</div>
);

export const SectionTitle = ({ children, right }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
    <h3 style={{ margin: 0, fontSize: 15, color: HRC.text, fontWeight: 700 }}>{children}</h3>
    {right}
  </div>
);

// Horizontal sub-tabs (violet pill on active).
export const SubTabs = ({ tabs, active, onChange }) => (
  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 16 }}>
    {tabs.map(t => {
      const key = t.key ?? t;
      const label = t.label ?? t;
      const on = key === active;
      return (
        <button key={key} onClick={() => onChange(key)}
          style={{
            padding: '7px 14px', border: 'none', cursor: 'pointer', borderRadius: 8, fontSize: 13,
            background: on ? HRC.violet : 'transparent', color: on ? HRC.navy : '#666',
            fontWeight: on ? 700 : 500, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
          {label}
          {t.badge != null && t.badge !== 0 && (
            <span style={{ background: HRC.red, color: '#fff', borderRadius: 10, padding: '0 6px', fontSize: 10, fontWeight: 700 }}>{t.badge}</span>
          )}
        </button>
      );
    })}
  </div>
);

// Table cells.
export const Th = ({ children, style }) => (
  <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 10, color: '#888', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `1px solid ${HRC.border}`, whiteSpace: 'nowrap', ...style }}>{children}</th>
);
export const Td = ({ children, style }) => (
  <td style={{ padding: '8px 10px', fontSize: 13, color: HRC.text, borderBottom: `1px solid #F1F0EE`, ...style }}>{children}</td>
);

export const Table = ({ children, style }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', ...style }}>{children}</table>
  </div>
);

// Coloured status pill.
const PILL = {
  active: HRC.green, present: HRC.green, approved: HRC.green, finalized: HRC.green, paid: HRC.green, published: HRC.green,
  accepted: HRC.green,
  pending: HRC.amber, draft: HRC.amber, half_day: HRC.amber, on_leave: HRC.amber, leave: HRC.navy,
  pending_reliever: HRC.amber, pending_hr: '#8B5CF6',
  rejected: HRC.red, absent: HRC.red, cancelled: HRC.red, closed: HRC.muted, inactive: HRC.muted,
  declined: HRC.red, declined_by_reliever: HRC.red, rejected_by_hr: HRC.red,
  week_off: HRC.muted, holiday: '#8B5CF6', exited: HRC.muted,
};

// Friendlier labels for the reliever-workflow statuses (otherwise the Pill just
// title-cases the raw value, e.g. an unhelpful "Pending hr").
export const STATUS_LABEL = {
  pending_reliever: 'Awaiting Reliever',
  declined_by_reliever: 'Reliever Declined',
  pending_hr: 'Pending HR',
  rejected_by_hr: 'Rejected by HR',
};

export const Pill = ({ value, label }) => {
  const c = PILL[value] || HRC.navy;
  return <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700,
    color: c, background: c + '1A', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{label ?? STATUS_LABEL[value] ?? String(value || '').replace(/_/g, ' ')}</span>;
};

// Buttons.
export const Btn = ({ children, onClick, variant = 'primary', size, disabled, style, type }) => {
  const base = { padding: size === 'sm' ? '5px 11px' : '8px 16px', borderRadius: 7, fontSize: size === 'sm' ? 12 : 13,
    cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 600, border: 'none', opacity: disabled ? 0.55 : 1, whiteSpace: 'nowrap' };
  const v = {
    primary: { background: HRC.navy, color: '#fff' },
    danger: { background: HRC.red, color: '#fff' },
    ghost: { background: 'transparent', color: HRC.navy, border: `1px solid ${HRC.border}` },
    soft: { background: HRC.violet, color: HRC.navy },
  }[variant];
  return <button type={type || 'button'} onClick={onClick} disabled={disabled} style={{ ...base, ...v, ...style }}>{children}</button>;
};

// KPI stat tile.
export const Stat = ({ label, value, sub, accent = HRC.navy, onClick }) => (
  <div onClick={onClick} style={{ background: '#fff', borderRadius: 10, border: `1px solid ${HRC.border}`,
    padding: '14px 16px', cursor: onClick ? 'pointer' : 'default', borderLeft: `3px solid ${accent}` }}>
    <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color: HRC.text, marginTop: 4 }}>{value}</div>
    {sub != null && <div style={{ fontSize: 11, color: HRC.muted, marginTop: 2 }}>{sub}</div>}
  </div>
);

// Form field.
export const Field = ({ label, children }) => (
  <label style={{ display: 'block' }}>
    <span style={{ fontSize: 10, color: '#888', fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>{label}</span>
    {children}
  </label>
);
export const input = { padding: '8px 10px', border: `1px solid #ddd`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box', width: '100%' };
export const MonthPicker = ({ value, onChange }) => (
  <input type="month" value={value} onChange={e => onChange(e.target.value)}
    style={{ ...input, width: 'auto', padding: '7px 10px' }} />
);

// Modal.
export const Modal = ({ title, onClose, children, width = 520 }) => (
  <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,20,40,0.45)', zIndex: 200,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 16px', overflowY: 'auto' }}>
    <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: width, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: `1px solid ${HRC.border}` }}>
        <h3 style={{ margin: 0, fontSize: 16, color: HRC.text }}>{title}</h3>
        <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: '#999', lineHeight: 1 }}>×</button>
      </div>
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  </div>
);

export const Empty = ({ children }) => (
  <div style={{ textAlign: 'center', color: HRC.muted, fontSize: 13, padding: '40px 0' }}>{children || 'No records found'}</div>
);
export const Loading = () => (
  <div style={{ textAlign: 'center', color: HRC.muted, fontSize: 13, padding: '40px 0' }}>Loading…</div>
);
